import { memo, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Play, CheckCheck, Code, RefreshCw, Lock, Terminal, Share2 } from "lucide-react";

const animationStyles = `
  @keyframes fadeInBlock {
    from { 
      opacity: 0; 
      clip-path: inset(0 100% 0 0);
      transform: translateX(-10px);
    }
    to { 
      opacity: 1; 
      clip-path: inset(0 0% 0 0);
      transform: translateX(0);
    }
  }
  @keyframes fadeInText {
    from { 
      opacity: 0; 
      transform: translateX(-8px); 
    }
    to { 
      opacity: 1; 
      transform: translateX(0); 
    }
  }
`;

interface QueryToolbarProps {
  onRun: () => void;
  onCheck?: () => void;
  onFormat: () => void;
  onReset: () => void;
  onCopy: () => void;
  onDebug?: () => void;
  isMobile?: boolean;
  isCheckLocked?: boolean;
  onCheckLocked?: () => void;
  showCheck?: boolean;
  executionTimeMs?: number | null;
  memoryUsageBytes?: number | null;
  readBytes?: number | null;
  readRows?: number | null;
  writtenBytes?: number | null;
  writtenRows?: number | null;
  resultBytes?: number | null;
  resultRows?: number | null;
  cpuTimeUs?: number | null;
}

interface TooltipButtonProps {
  onClick: () => void;
  icon: React.ReactNode;
  tooltip: string;
  tooltipId: string;
}

const TooltipButton = ({ onClick, icon, tooltip, tooltipId }: TooltipButtonProps) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (showTooltip && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();

      // Базовая позиция — под кнопкой
      let top = rect.bottom + 8;
      const right = window.innerWidth - rect.right;

      // Оценка высоты tooltip (текстовый, ~32–40px)
      const ESTIMATED_HEIGHT = 40;

      // Гарантируем, что tooltip не окажется под фиксированным Header
      const header = document.querySelector("header");
      if (header instanceof HTMLElement) {
        const headerRect = header.getBoundingClientRect();
        const minTop = headerRect.bottom + 8;
        if (top < minTop) top = minTop;
      }

      // Если tooltip не помещается снизу — показываем его над кнопкой
      const maxTop = window.innerHeight - ESTIMATED_HEIGHT - 8;
      if (top > maxTop) {
        const aboveTop = rect.top - ESTIMATED_HEIGHT - 8;
        top = Math.max(aboveTop, 8);
      }

      setTooltipPosition({ top, right });
    }
  }, [showTooltip]);

  return (
    <>
      <div 
        ref={buttonRef}
        className="relative group"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <button
          onClick={onClick}
          className="flex items-center justify-center w-7 h-7 bg-neutral-light text-neutral-text rounded-[5px] hover:bg-neutral-border active:scale-95 transition-transform border border-neutral-border"
          aria-describedby={tooltipId}
        >
          {icon}
        </button>
      </div>
      {showTooltip && buttonRef.current && createPortal(
        <div
          id={tooltipId}
          className="fixed bg-neutral-text text-white text-xs rounded-lg px-3 py-2 shadow-xl whitespace-nowrap pointer-events-none z-[9999]"
          style={{
            top: `${tooltipPosition.top}px`,
            right: `${tooltipPosition.right}px`,
            transform: 'translateX(0)',
          }}
          role="tooltip"
        >
          {tooltip}
          <div className="absolute -top-1 right-4 w-2 h-2 bg-neutral-text rotate-45"></div>
        </div>,
        document.body
      )}
    </>
  );
};

const formatExecutionTime = (ms: number | null | undefined): string | null => {
  if (ms === null || ms === undefined) return null;
  if (ms < 1) return "< 1ms";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

const formatBytes = (bytes: number | null | undefined): string | null => {
  if (bytes === null || bytes === undefined || bytes === 0) return null;
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)}KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)}MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)}GB`;
};

const formatRows = (rows: number | null | undefined): string | null => {
  if (rows === null || rows === undefined || rows === 0) return null;
  if (rows < 1000) return `${rows}`;
  if (rows < 1000000) return `${(rows / 1000).toFixed(1)}K`;
  return `${(rows / 1000000).toFixed(1)}M`;
};

const formatCpuTime = (us: number | null | undefined): string | null => {
  if (us === null || us === undefined || us === 0) return null;
  if (us < 1000) return `${Math.round(us)}µs`;
  if (us < 1000000) return `${(us / 1000).toFixed(2)}ms`;
  return `${(us / 1000000).toFixed(2)}s`;
};

export const QueryToolbar = memo(({
  onRun,
  onCheck,
  onFormat,
  onReset,
  onCopy,
  onDebug,
  isMobile = false,
  isCheckLocked = false,
  onCheckLocked,
  showCheck = true,
  executionTimeMs,
  memoryUsageBytes,
  readBytes,
  readRows,
  writtenBytes,
  writtenRows,
  resultBytes,
  resultRows,
  cpuTimeUs,
}: QueryToolbarProps) => {
  const [showDebugTooltip, setShowDebugTooltip] = useState(false);
  const [isHoveringDebug, setIsHoveringDebug] = useState(false);
  const debugButtonRef = useRef<HTMLDivElement>(null);
  const blockRef = useRef<HTMLDivElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, right: 0 });

  // Обновляем позицию tooltip для метрик и учитываем высоту Header
  useEffect(() => {
    if (isHoveringDebug && blockRef.current) {
      const rect = blockRef.current.getBoundingClientRect();
      let top = rect.bottom + 8;
      const right = window.innerWidth - rect.right;

      const ESTIMATED_HEIGHT = 56;

      const header = document.querySelector("header");
      if (header instanceof HTMLElement) {
        const headerRect = header.getBoundingClientRect();
        const minTop = headerRect.bottom + 8;
        if (top < minTop) top = minTop;
      }

      const maxTop = window.innerHeight - ESTIMATED_HEIGHT - 8;
      if (top > maxTop) {
        const aboveTop = rect.top - ESTIMATED_HEIGHT - 8;
        top = Math.max(aboveTop, 8);
      }

      setTooltipPosition({ top, right });
    }
  }, [isHoveringDebug]);

  const hasElapsed = formatExecutionTime(executionTimeMs);
  const hasMetrics = hasElapsed || formatRows(readRows) || formatBytes(readBytes) || formatBytes(memoryUsageBytes) || formatRows(resultRows) || formatBytes(resultBytes) || formatCpuTime(cpuTimeUs);

  return (
    <>
      <style>{animationStyles}</style>
      <div className={`${isMobile ? "px-4 pt-4 pb-4" : "p-4"}`}>
        <div className={`flex ${isMobile ? "flex-row gap-2 items-center justify-between" : "justify-between items-center flex-wrap gap-2"}`}>
          {isMobile ? (
            <>
              {/* Слева: Проверить */}
              {showCheck && onCheck && (
                <button
                  data-onboarding-id="check-button"
                  onClick={isCheckLocked ? onCheckLocked : onCheck}
                  className={`flex items-center justify-center h-[38px] px-4 text-white font-medium rounded-[5px] active:scale-95 transition-colors ${isCheckLocked ? "opacity-60 cursor-not-allowed" : "hover:opacity-90"}`}
                  style={{ backgroundColor: "#1E74FE" }}
                >
                  {isCheckLocked ? <Lock size={16} className="mr-1.5" /> : <CheckCheck size={16} className="mr-1.5" />} Проверить
                </button>
              )}

              {/* Справа: Trace, Запуск */}
              <div className="flex flex-row gap-2 items-center justify-end">
                {/* Trace - черная квадратная кнопка */}
                {onDebug && (
                  <div ref={debugButtonRef} className="relative">
                    <button
                      data-onboarding-id="trace-debug-button"
                      onClick={onDebug}
                      className="h-[38px] w-[38px] flex items-center justify-center rounded-[5px] hover:opacity-90 active:scale-95 transition-transform border border-gray-700 hover:border-gray-600 shadow-sm"
                      style={{ backgroundColor: "#1e1e1e" }}
                    >
                      <Terminal size={18} className="text-white" />
                    </button>
                  </div>
                )}

                {/* Запуск */}
                <button
                  data-onboarding-id="run-button"
                  onClick={onRun}
                  className="flex items-center justify-center h-[40px] px-4 text-white font-medium rounded-[5px] hover:opacity-90 active:scale-95 transition-transform"
                  style={{ backgroundColor: "#1AB74C" }}
                >
                  <Play size={16} className="mr-1.5" /> Запуск
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Левая группа: Запуск, Проверить, Trace (с elapsed) */}
              <div className="flex gap-2 items-center">
                {/* Запуск */}
                <button
                  data-onboarding-id="run-button"
                  onClick={onRun}
                  className="flex items-center justify-center h-[40px] px-4 text-white font-medium rounded-[5px] hover:opacity-90 active:scale-95 transition-transform"
                  style={{ backgroundColor: "#1AB74C" }}
                >
                  <Play size={16} className="mr-1.5" /> Запуск
                </button>

                {/* Проверить */}
                {showCheck && onCheck && (
                  <button
                    data-onboarding-id="check-button"
                    onClick={isCheckLocked ? onCheckLocked : onCheck}
                    className={`flex items-center justify-center h-[38px] px-4 text-white font-medium rounded-[5px] active:scale-95 transition-colors ${isCheckLocked ? "opacity-60 cursor-not-allowed" : "hover:opacity-90"}`}
                    style={{ backgroundColor: "#1E74FE" }}
                  >
                    {isCheckLocked ? <Lock size={16} className="mr-1.5" /> : <CheckCheck size={16} className="mr-1.5" />} Проверить
                  </button>
                )}

                {/* Trace - интерактивная кнопка с метриками */}
                {onDebug && (
                  <div
                    ref={blockRef}
                    className="relative group"
                    onMouseEnter={() => {
                      setIsHoveringDebug(true);
                      setShowDebugTooltip(true);
                    }}
                    onMouseLeave={() => {
                      setIsHoveringDebug(false);
                      setShowDebugTooltip(false);
                    }}
                  >
                    <div className="flex items-center rounded-[5px] border border-gray-700 hover:border-gray-600 shadow-sm overflow-hidden">
                      <button
                        data-onboarding-id="trace-debug-button"
                        onClick={onDebug}
                        className="h-[38px] w-[38px] flex items-center justify-center hover:opacity-90 active:scale-95 transition-transform flex-shrink-0"
                        style={{ backgroundColor: "#1e1e1e" }}
                      >
                        <Terminal size={18} className="text-white" />
                      </button>
                      <div 
                        className="h-[38px] flex items-center bg-white border-l border-gray-300 overflow-hidden"
                        style={{ 
                          maxWidth: hasElapsed ? '200px' : '0px',
                          width: hasElapsed ? 'auto' : '0px',
                          transition: 'max-width 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), width 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.6s ease-in-out, padding-left 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), padding-right 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                          opacity: hasElapsed ? 1 : 0,
                          paddingLeft: hasElapsed ? '12px' : '0px',
                          paddingRight: hasElapsed ? '12px' : '0px',
                          pointerEvents: hasElapsed ? 'auto' : 'none'
                        }}
                      >
                        {hasElapsed && (
                          <span className="text-neutral-text text-xs font-mono whitespace-nowrap">
                            elapsed: {hasElapsed}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Правая группа: форматирование */}
              <div className="flex gap-2 items-center">
                <TooltipButton
                  onClick={onFormat}
                  icon={<Code size={16} />}
                  tooltip="Форматировать SQL-запрос"
                  tooltipId="format-tooltip"
                />
                <TooltipButton
                  onClick={onReset}
                  icon={<RefreshCw size={16} />}
                  tooltip="Очистить результаты запроса"
                  tooltipId="reset-tooltip"
                />
                <TooltipButton
                  onClick={onCopy}
                  icon={<Share2 size={16} />}
                  tooltip="Скопировать ссылку на запрос"
                  tooltipId="copy-tooltip"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tooltip для кнопки Debug через портал */}
      {showDebugTooltip && !isMobile && blockRef.current && createPortal(
        <div
          className="fixed bg-neutral-text text-white text-xs rounded-lg px-3 py-2 shadow-xl whitespace-nowrap pointer-events-none z-[9999]"
          style={{
            top: `${tooltipPosition.top}px`,
            right: `${tooltipPosition.right}px`,
            transform: 'translateX(0)',
          }}
        >
          {hasMetrics ? (
            <div className="flex flex-col gap-1">
              {hasElapsed && (
                <div className="flex items-center gap-2">
                  <span className="text-neutral-300">elapsed:</span>
                  <span className="font-mono">{hasElapsed}</span>
                </div>
              )}
              {formatRows(readRows) && (
                <div className="flex items-center gap-2">
                  <span className="text-neutral-300">read_rows:</span>
                  <span className="font-mono">{formatRows(readRows)}</span>
                </div>
              )}
              {formatBytes(readBytes) && (
                <div className="flex items-center gap-2">
                  <span className="text-neutral-300">read_bytes:</span>
                  <span className="font-mono">{formatBytes(readBytes)}</span>
                </div>
              )}
              {formatBytes(memoryUsageBytes) && (
                <div className="flex items-center gap-2">
                  <span className="text-neutral-300">memory:</span>
                  <span className="font-mono">{formatBytes(memoryUsageBytes)}</span>
                </div>
              )}
              {formatRows(resultRows) && (
                <div className="flex items-center gap-2">
                  <span className="text-neutral-300">result_rows:</span>
                  <span className="font-mono">{formatRows(resultRows)}</span>
                </div>
              )}
              {formatBytes(resultBytes) && (
                <div className="flex items-center gap-2">
                  <span className="text-neutral-300">result_bytes:</span>
                  <span className="font-mono">{formatBytes(resultBytes)}</span>
                </div>
              )}
              {formatCpuTime(cpuTimeUs) && (
                <div className="flex items-center gap-2">
                  <span className="text-neutral-300">cpu_time:</span>
                  <span className="font-mono">{formatCpuTime(cpuTimeUs)}</span>
                </div>
              )}
            </div>
          ) : (
            "Дебаг режим с анализом трейса"
          )}
          <div className="absolute -top-1 right-4 w-2 h-2 bg-neutral-text rotate-45"></div>
        </div>,
        document.body
      )}
    </>
  );
});
